import React from 'react';
import { toast } from 'react-hot-toast';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import OwnershipDropdown from './OwnershipDropdown';
import { CompanyOwnershipDisplay } from '~/constants';
import { useUpdateOwnership } from '~/mutations/admin/useUpdateOwnership';
import { usePageType, useProjectContext } from '~/hooks';
import { useAdminUsersQuery } from '~/requests/admin/useAdminUsersQuery';
import { ownershipKey, useOwnershipQuery } from '~/requests/ownership/useOwnershipQuery';
import { User } from '~/types/user';

export default function OwnershipPicker() {
  const queryClient = useQueryClient();
  const pageType = usePageType();
  const { id } = useParams<{ id: string }>();
  const { project } = useProjectContext();
  const entityType = project?.portfolioId ? 'portfolio' : pageType;
  const entityId = project?.portfolioId ?? id;
  const typeDisplay = entityType.charAt(0).toUpperCase() + entityType.slice(1);

  const { data: ownershipData } = useOwnershipQuery();
  const { data: users } = useAdminUsersQuery();
  const { mutateAsync: updateOwnership } = useUpdateOwnership();

  const owner = ownershipData?.owner;

  const handleChangeOwner = async (
    e: React.SyntheticEvent<HTMLElement>,
    value: string,
    users: User[]
  ) => {
    const userId = value as string;
    if (userId !== owner.id) {
      // Change to the new owner
      queryClient.setQueryData(ownershipKey(entityType, entityId), {
        ...ownershipData,
        owner: users?.find((user: User) => user.id === userId),
        ownerId: userId
      });
      try {
        await updateOwnership({
          entityType,
          entityId,
          userId
        });
        toast.success('Successfully updated ownership data.');
      } catch (error) {
        console.error('Error updating ownership:', error);
        toast.error('Failed to update ownership data. Please try again');
        // Reset the owner
        queryClient.setQueryData(ownershipKey(entityType, entityId), {
          ...ownershipData,
          owner,
          ownerId: owner.id
        });
      }
    }
  };

  if (!owner || !users?.length) {
    return null;
  }

  return (
    <div className="ownership-picker-container">
      <OwnershipDropdown
        typeDisplay={typeDisplay}
        ownershipText={CompanyOwnershipDisplay.OWNER}
        defaultValue={owner.id}
        buttonText={owner.name}
        options={users?.map((user) => ({
          key: user?.id,
          text: user?.name,
          value: user?.id
        }))}
        onChange={(e, value) => handleChangeOwner(e, value, users)}
      />
    </div>
  );
}
